// Carga de documentos desde asesoras, en dos modos:
// - "many": como lo hace el estudiante en klazia.com, un archivo que puede
//   contener varios documentos (requirements_many), siempre escaneado.
// - "single": como "+ Requisito" en admin, un documento con método físico o
//   escaneado y archivo opcional.
// En ambos queda "Pendiente": aprobar o rechazar es de administración.
import React from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react'

import xhr from 'xhr'

export const stripHtml = (html) => String(html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()

const MAX_MB = 15

export default function UploadDocumentModal({ mode, isOpen, onClose, requirements, studentId, programTypeId, onSaved }) {
  const toast = useToast()
  const isMany = mode === 'many'
  const [selected, setSelected] = React.useState([])
  const [single, setSingle] = React.useState('')
  const [method, setMethod] = React.useState('scanned')
  const [file, setFile] = React.useState(null)
  const [submitted, setSubmitted] = React.useState(false)
  const [saving, setSaving] = React.useState(false)

  React.useEffect(() => {
    if (!isOpen) return
    setSelected([])
    setSingle('')
    setMethod('scanned')
    setFile(null)
    setSubmitted(false)
  }, [isOpen])

  const fileRequired = isMany || method === 'scanned'
  const errors = {
    docs: isMany ? selected.length === 0 : !single,
    file: fileRequired && !file,
    size: file && file.size > MAX_MB * 1024 * 1024,
  }

  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((el) => el !== id) : [...prev, id]))

  const handleSave = async () => {
    setSubmitted(true)
    if (errors.docs || errors.file || errors.size) return
    try {
      setSaving(true)
      const res = await xhr.post('/requirement-sittings/adviser', {
        data: {
          student: Number(studentId),
          program_type: Number(programTypeId) || null,
          method: isMany ? 'scanned' : method,
          ...(isMany ? { requirements_many: selected } : { requirement: Number(single) }),
        },
      })
      if (file) {
        const formData = new FormData()
        formData.append('files', file)
        formData.append('ref', 'api::requirement-sitting.requirement-sitting')
        formData.append('refId', res.data.id)
        formData.append('field', 'file')
        await xhr('/upload', { method: 'post', data: formData, headers: { 'Content-Type': 'multipart/form-data' } })
      }
      toast({ title: 'Documento cargado. Queda pendiente de revisión.', status: 'success', duration: 3000, isClosable: true })
      onSaved?.()
      onClose()
    } catch (error) {
      console.error(error)
      toast({
        title: error?.response?.data?.error?.message || 'No se pudo cargar el documento.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isMany ? 'Subir documento' : 'Añadir requisito'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={5}>
            <Text fontSize="sm" color="gray.600">
              {isMany
                ? 'Sube el archivo que te envió el estudiante y marca qué documentos contiene. Queda pendiente hasta que administración lo revise.'
                : 'Registra un requisito puntual, entregado escaneado o en físico. Queda pendiente hasta que administración lo revise.'}
            </Text>

            {isMany ? (
              <FormControl isRequired isInvalid={submitted && errors.docs}>
                <FormLabel fontSize="sm">¿Qué documentos contiene el archivo?</FormLabel>
                <Stack spacing={2} maxH="220px" overflowY="auto" border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
                  {requirements.map((el) => (
                    <Checkbox key={el.id} colorScheme="purple" isChecked={selected.includes(el.id)} onChange={() => toggle(el.id)}>
                      <Text fontSize="sm">{stripHtml(el.name)}</Text>
                    </Checkbox>
                  ))}
                </Stack>
                <FormErrorMessage>Marca al menos un documento.</FormErrorMessage>
              </FormControl>
            ) : (
              <>
                <FormControl isRequired isInvalid={submitted && errors.docs}>
                  <FormLabel fontSize="sm">Requisito</FormLabel>
                  <Select placeholder="Elige el requisito" value={single} onChange={(e) => setSingle(e.target.value)}>
                    {requirements.map((el) => (
                      <option key={el.id} value={el.id}>
                        {stripHtml(el.name)}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>Elige el requisito.</FormErrorMessage>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Método de entrega</FormLabel>
                  <Select value={method} onChange={(e) => setMethod(e.target.value)}>
                    <option value="scanned">Escaneado</option>
                    <option value="fisical">Físico</option>
                  </Select>
                </FormControl>
              </>
            )}

            <FormControl isRequired={fileRequired} isInvalid={submitted && (errors.file || errors.size)}>
              <FormLabel fontSize="sm">Archivo</FormLabel>
              <Input
                type="file"
                p={1}
                accept="image/*,application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              {!(submitted && (errors.file || errors.size)) && (
                <FormHelperText>
                  PDF o imagen, máximo {MAX_MB} MB.{!fileRequired && ' Opcional si se entregó en físico.'}
                </FormHelperText>
              )}
              <FormErrorMessage>
                {errors.size ? `El archivo pesa más de ${MAX_MB} MB.` : 'Adjunta el archivo.'}
              </FormErrorMessage>
            </FormControl>
            {file && (
              <Box fontSize="xs" color="gray.500" mt={-3}>
                {file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB
              </Box>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button colorScheme="purple" onClick={handleSave} isLoading={saving}>
            {isMany ? 'Subir documento' : 'Añadir requisito'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
